import React from "react";
import cx from "classnames";
import EventUtils from "../../utils/EventUtils";
import IDUtils from "../../utils/IDUtils";
import UIActions from "../../actions/UIActions";
import TransformerStore from "../../stores/TransformerStore";
import AppConstants from "../../constants/AppConstants";
import WordpressFieldLabel from "../utils/WordpressFieldLabel";
import FormFieldLabel from "../utils/FormFieldLabel";
import CalloutLabel from "../utils/CalloutLabel";
import { CreateSnapPoints } from "../../utils/Snapping/SnappingUtils";
import uiPrefix from "../../utils/UiPrefixUtils";
import { LINE_THICKNESS } from "../../constants/MergeConstants";
import { getProp } from "../../utils/Merge/MergeUtils.mjs";
import ElementUtils from "@/utils/ElementUtils";
import { positionElementToContainer } from "@/utils/GroupUtils";

const getDimensions = (element, props) => {
  const minHeight = ElementUtils.getMinHeight(element, props.layout, props.theme);
  const minWidth = ElementUtils.getMinWidth(element, props.layout, props.theme);

  const elemProps = element.get(props.layout);
  const baseHeight = elemProps.get("h", 1);
  const baseWidth = elemProps.get("w", 1);

  let elementDimensions = {
    x: elemProps.get("x"),
    y: elemProps.get("y"),
    w: baseWidth < minWidth ? minWidth : baseWidth,
    h: baseHeight < minHeight ? minHeight : baseHeight,
    a: elemProps.get("a"),
  };

  if (props.offsets) {
    elementDimensions = positionElementToContainer(elementDimensions, props.offsets, true);
  }

  return elementDimensions;
};

class ElementSelect extends React.Component {
  constructor(props) {
    super(props);

    const groupParent = props.groupParent;
    const element = groupParent || props.element;

    const { x, y, w, h, a } = getDimensions(element, props);

    const snapPoints = CreateSnapPoints({ x, y, w, h, a });
    this.state = {
      x,
      y,
      w,
      h,
      a,
      element: props.element,
      snapPoints,
    };
  }

  // called after a component has been updated and re-rendered
  componentDidUpdate(prevProps, prevState) {
    const isSelected = this.props.selected;
    const wasSelected = prevProps.selected;

    if (isSelected && !wasSelected) {
      this.transformUnsubscribe = TransformerStore.listen(this._onTransform);
    } else if (!isSelected && wasSelected) {
      if (this.transformUnsubscribe) {
        this.transformUnsubscribe();
        this.transformUnsubscribe = null;
      }
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.element !== state.element) {
      const { x, y, w, h, a } = getDimensions(props.element, props);
      const snapPoints = CreateSnapPoints({ x, y, w, h, a });
      return {
        x,
        y,
        w,
        h,
        a,
        element: props.element,
        snapPoints,
      };
    }

    return null;
  }

  // called before component is removed from DOM, perform cleanup here
  componentWillUnmount() {
    if (this.transformUnsubscribe) {
      this.transformUnsubscribe();
      this.transformUnsubscribe = null;
    }
  }

  _onTransform = (data) => {
    const position = TransformerStore.getElementPosition(this.props.id);
    if (!position) return;

    const { x, y, w, h, a } = position;
    this.setState({ x, y, w, h, a });
  };

  _onEditFormFieldLabel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    UIActions.toggleElementPropertiesSection({
      name: AppConstants.PANEL_SECTION_ELEMENT_TEXT_FORM,
    });
  };

  _onMouseOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    UIActions.itemOver(IDUtils.getID(this.props.id, this.props.layout));
  };

  _selectElement = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const { blockId, id, layout, mode, element: elem } = this.props;

    const selection = EventUtils.selectionType(e);

    UIActions.itemOut();
    UIActions.selectElement({
      id,
      type: elem.get("type"),
      layout,
      blockId,
      addTo: selection.multi,
      addRange: selection.range,
      from: "stage",
    });

    this.props.onSelect && this.props.onSelect();
  };

  /**
   * A touch or pen press on an element in a block that isn't selected:
   * selects the block and the element at once. A mouse press is left to the
   * block, which it selects; hovering it showed the block first.
   * @param {React.PointerEvent} e
   */
  _activateAndSelect = (e) => {
    if (e.pointerType === "mouse") return;
    this.props.onActivate();
    this._selectElement(e);
  };

  _onMouseOut = (e) => {
    e.preventDefault();
    e.stopPropagation();

    UIActions.itemOut();
  };

  _captureClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  render() {
    const {
      blockId,
      editing,
      element: elem,
      enabled,
      id,
      highlight,
      layout,
      mode,
      selected,
    } = this.props;

    const domId = IDUtils.getDOMID("si_element", layout, id);
    const elementType = elem.get("type");

    let label = null;

    if (selected) {
      let labelType = null;

      let isFormField = elem.getIn(["form", "isField"], false);
      let wpField = elem.getIn(["wp", "field"]);

      if (wpField) {
        labelType = <WordpressFieldLabel element={elem} />;
      } else if (isFormField && !editing) {
        labelType = <FormFieldLabel element={elem} onClick={this._onEditFormFieldLabel} />;
      }

      if (labelType) {
        label = (
          <CalloutLabel style={{ position: "absolute", top: "-30px" }}>{labelType}</CalloutLabel>
        );
      }
    }

    // if line is merged, set height value as desktop height
    const isLineType = elementType === AppConstants.ELEMENT_TYPE_LINE;

    let style = {
      position: "absolute",
      width: this.state.w,
      height: isLineType ? getProp(elem, layout, LINE_THICKNESS) + 20 : this.state.h,
      top: isLineType ? this.state.y - 10 : this.state.y,
      left: this.state.x,
      transform: `rotate(${this.state.a}deg)`,
    };

    const elemClasses = cx({
      [uiPrefix(`element-type-${elementType}`)]: true,
      "highlight-border": highlight,
      locked: elem.get(AppConstants.LOCK_EDITING_KEY) === "1",
      selectable: true,
      selected,
    });

    let mouseOverAction = null;
    let mouseOutAction = null;
    let mouseDownAction = null;
    let mouseClickAction = null;

    if (enabled) {
      mouseOverAction = this._onMouseOver;
      mouseOutAction = this._onMouseOut;
      mouseDownAction = this._selectElement;
      mouseClickAction = this._captureClick;
    } else if (this.props.onActivate) {
      mouseDownAction = this._activateAndSelect;
    }

    return (
      <>
        <div
          id={domId}
          data-id={id}
          data-type={elementType}
          data-selected={selected ? 1 : 0}
          data-snap-points={this.state.snapPoints}
          data-block-id={blockId}
          data-parent-id={elem.get("pId") ?? null}
          style={style}
          data-e2e={uiPrefix(`element-type-${elementType}`)}
          className={elemClasses}
          onClick={mouseClickAction}
          onMouseEnter={mouseOverAction}
          onMouseLeave={mouseOutAction}
          onPointerDown={mouseDownAction}
        >
          {label}
        </div>
      </>
    );
  }
}

export default ElementSelect;
